import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import z from "zod";

import { Button } from "@/components/Button/Button";
import { Calendar, type CalendarProps } from "@/components/Calendar/Calendar";
import { PersistentDrawer } from "@/components/Drawer";
import { useDatesGuard } from "@/features/search/hooks/useDatesGuard";

const NUMBER_OF_MONTHS = 12;

const datesSelectionFormSchema = z.object({
  isRange: z.boolean(),
  dates: z.object({
    from: z.date().nullable(),
    to: z.date().nullable(),
  }),
});

type DatesSelectionFormValues = z.infer<typeof datesSelectionFormSchema>;

export type DatesSelectionProps = {
  initialValues: DatesSelectionFormValues;
  onConfirm: (values: DatesSelectionFormValues) => void;
  renderRangePicker?: (props: {
    value: boolean;
    onChange: (newValue: boolean) => void;
  }) => React.ReactNode;
  fromLabel?: string;
  toLabel?: string;
  calendarProps?: Partial<
    Omit<CalendarProps, "startField" | "endField" | "setFieldValue">
  >;
};

export function DatesSelection({
  initialValues,
  onConfirm,
  renderRangePicker,
  calendarProps,
  fromLabel,
  toLabel,
}: DatesSelectionProps) {
  const intl = useIntl();

  const form = useForm<DatesSelectionFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(datesSelectionFormSchema),
  });

  const [isRange, from, to] = useWatch({
    control: form.control,
    name: ["isRange", "dates.from", "dates.to"],
  });

  useDatesGuard({
    departureDate: from,
    returnDate: to,
    setReturnDate: (date) => form.setValue("dates.to", date),
  });

  // Read values inside the effect (not as deps) so this only fires on isRange
  // transitions, not when `to` becomes null mid-selection (e.g. hotels resetting
  // checkout after the user picks a new check-in date).
  useEffect(() => {
    if (!isRange) return;
    const { from: currentFrom, to: currentTo } = form.getValues("dates");
    if (!currentTo && currentFrom) {
      form.setValue("dates.to", dayjs(currentFrom).add(1, "day").toDate());
    }
  }, [isRange, form]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit(onConfirm)(event);
      }}
      className="flex min-h-0 shrink grow basis-auto flex-col gap-2"
    >
      {renderRangePicker?.({
        value: isRange,
        onChange: (newValue) => form.setValue("isRange", newValue),
      })}

      <Calendar
        disabledDates={{ before: new Date() }}
        startMonth={new Date()}
        numberOfMonths={NUMBER_OF_MONTHS}
        {...calendarProps}
        startField={{
          label: fromLabel,
          value: from || undefined,
          path: "dates.from",
        }}
        endField={
          isRange
            ? {
                label: toLabel,
                value: to || undefined,
                path: "dates.to",
              }
            : undefined
        }
        setFieldValue={(path, value) => {
          form.setValue(path as "dates.from" | "dates.to", value ?? null);
        }}
      />

      <PersistentDrawer>
        <div className="mt-auto">
          <Button
            disabled={isRange ? !from || !to : !from}
            fullWidth
            variant="contained"
            size="medium"
            type="submit"
          >
            {intl.formatMessage({
              defaultMessage: "Confirm",
              description:
                "Generic 'Confirm' button label to submit or accept an action",
            })}
          </Button>
        </div>
      </PersistentDrawer>
    </form>
  );
}
